import { Link } from 'react-router-dom';
import type { FC } from 'react';
import Canonical from '../../seo/Canonical';
import '../Homepage/FAQ.css';
import './Logistics.css';
import LogisticsPageHero from './LogisticsPageHero';
import Newsletter from '../Homepage/Newsletter';
import Footer from '../Layouts/Footer';
import FAQSection from '../FAQSection';
import RelatedLinks from '../Common/RelatedLinks';
import PageLastUpdated from '../Common/PageLastUpdated';
import { useRevealClass } from '../../hooks/useInViewOnce';
import { trackWhatsAppClick } from '../../utils/trackLead';
import ContactForm from '../Common/ContactForm';
import {
  COMPANY_INTEGRATIONS_COUNT,
  COMPANY_PICKING_ACCURACY,
  COMPANY_PICK_PACK_FROM,
  COMPANY_SHIPPING_FROM,
} from '../../seo/companyFacts';
import {
  logisticsClusterConfigs,
  type LogisticsClusterId,
} from './logisticsClusterData';

const HERO_IMAGE = '/logisticsimages/LOGISTICS_HERO_IMAGE.jpg';

type Props = {
  clusterId: LogisticsClusterId;
  customSEOBlock?: React.ReactNode;
  summaryBlock?: React.ReactNode;
};

const LogisticsClusterPage: FC<Props> = ({ clusterId, customSEOBlock, summaryBlock }) => {
  const cfg = logisticsClusterConfigs[clusterId];
  const servicesRevealRef = useRevealClass('logistics-services--visible');
  const howRevealRef = useRevealClass('logistics-how--visible');

  const whatsappHref =
    'https://wa.me/447404049714?text=' + encodeURIComponent(cfg.whatsappPrefill);

  return (
    <div className="logistics-page">
      <Canonical />
      <LogisticsPageHero
        className="logistics-hero--no-overlay"
        ariaLabel={cfg.ariaLabel}
        imageSrc={HERO_IMAGE}
        imageAlt="UK fulfilment warehouse operations at Pick Pack Pro Milton Keynes"
        titleLight={cfg.titleLight}
        titleAccent={cfg.titleAccent}
        description={
          <>
            <PageLastUpdated path={cfg.path} className="page-last-updated--on-dark page-last-updated--hero" />
            <p>
              {cfg.ariaLabel} from Pick Pack Pro means barcode-led warehouse execution in Milton Keynes with a{' '}
              {COMPANY_PICKING_ACCURACY} picking accuracy target, {COMPANY_INTEGRATIONS_COUNT} channel integrations,
              pick and pack from {COMPANY_PICK_PACK_FROM}, and tracked shipping from {COMPANY_SHIPPING_FROM}.
            </p>
            <p>
              Prefer the full map of transport and fulfilment options? Start at our{' '}
              <Link to="/logistics-services">logistics services hub</Link>, compare{' '}
              <Link to="/pricing-fulfillment-services">fulfilment pricing UK</Link>, or read{' '}
              <Link to="/fulfillment-frequently-asked-questions">fulfilment FAQs</Link>.
            </p>
          </>
        }
        features={cfg.heroFeatures}
        primaryCta={{ label: cfg.ctaLabel, to: '/contact-fulfillment-services' }}
      />

      {summaryBlock}

      <section
        ref={servicesRevealRef}
        className="logistics-services"
        aria-labelledby={`${clusterId}-services-title`}
      >
        <div className="logistics-services-inner">
          <header className="logistics-services-header">
            <p className="logistics-services-eyebrow">What we deliver</p>
            <h2 id={`${clusterId}-services-title`} className="logistics-services-title">
              {cfg.ariaLabel} — Milton Keynes
            </h2>
            <p className="logistics-services-intro">
              {cfg.ariaLabel} at Pick Pack Pro is a focused warehouse lane — not a catch-all brochure — so buyers
              searching this intent find a clear service, SLA context and next step.
            </p>
          </header>

          {clusterId === 'ecommerce' ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: '32px', alignItems: 'start', marginTop: '24px' }}>
              <div className="logistics-tiles-grid" role="list" style={{ marginTop: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                {cfg.tiles.map((tile) => (
                  <article
                    key={tile.id}
                    className="logistics-tile logistics-tile-animate"
                    role="listitem"
                    aria-labelledby={`${clusterId}-tile-${tile.id}`}
                  >
                    <h3 id={`${clusterId}-tile-${tile.id}`} className="logistics-tile-title">
                      {tile.title}
                    </h3>
                    <p className="logistics-tile-copy">{tile.copy}</p>
                  </article>
                ))}
              </div>
              <div style={{ background: '#ffffff', padding: '24px', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' }}>
                <h3 style={{ fontSize: '1.4rem', color: '#0f172a', marginBottom: '20px', fontWeight: 700 }}>
                  Discuss Your Requirements
                </h3>
                <ContactForm source="ecommerce-fulfillment-services" isShowShopify={false} />
              </div>
            </div>
          ) : (
            <div className="logistics-tiles-grid" role="list">
              {cfg.tiles.map((tile) => (
                <article
                  key={tile.id}
                  className="logistics-tile logistics-tile-animate"
                  role="listitem"
                  aria-labelledby={`${clusterId}-tile-${tile.id}`}
                >
                  <h3 id={`${clusterId}-tile-${tile.id}`} className="logistics-tile-title">
                    {tile.title}
                  </h3>
                  <p className="logistics-tile-copy">{tile.copy}</p>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {customSEOBlock}

      <section className="logistics-page-section logistics-page-section--white" aria-labelledby={`${clusterId}-audience`}>
        <div className="logistics-page-section-inner">
          <header className="logistics-page-section-header">
            <h2 id={`${clusterId}-audience`} className="logistics-page-section-title">
              {cfg.audienceTitle}
            </h2>
            <p className="logistics-page-section-lead">{cfg.audienceLead}</p>
          </header>
          <ul className="logistics-hub-audience-list">
            {cfg.audiencePoints.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="logistics-page-section logistics-page-section--white">
        <div className="logistics-page-section-inner" style={{ padding: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '3rem', alignItems: 'start' }}>
            <div style={{ width: '100%', paddingRight: '1rem', boxSizing: 'border-box' }}>
              <section aria-labelledby={`${clusterId}-facts`}>
                <div className="logistics-page-section-inner" style={{ padding: 0 }}>
                  <header className="logistics-page-section-header">
                    <h2 id={`${clusterId}-facts`} className="logistics-page-section-title">
                      Citable operations facts
                    </h2>
                    <p className="logistics-page-section-lead">
                      These figures appear across Pick Pack Pro service pages and pricing so AI and search snippets can quote
                      the same numbers.
                    </p>
                  </header>
                  <table className="logistics-facts-table">
                    <caption className="visually-hidden">Pick Pack Pro fulfilment facts</caption>
                    <thead>
                      <tr>
                        <th scope="col">Metric</th>
                        <th scope="col">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <th scope="row">Picking accuracy target</th>
                        <td>{COMPANY_PICKING_ACCURACY}</td>
                      </tr>
                      <tr>
                        <th scope="row">Channel / cart integrations</th>
                        <td>{COMPANY_INTEGRATIONS_COUNT}</td>
                      </tr>
                      <tr>
                        <th scope="row">Pick and pack from</th>
                        <td>{COMPANY_PICK_PACK_FROM} per order</td>
                      </tr>
                      <tr>
                        <th scope="row">Tracked shipping from</th>
                        <td>{COMPANY_SHIPPING_FROM}</td>
                      </tr>
                      <tr>
                        <th scope="row">Warehouse</th>
                        <td>
                          <Link to="/fulfillment-centre-milton-keynes">Milton Keynes fulfilment centre</Link>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
            
            <div style={{ width: '100%', paddingLeft: '1rem', borderLeft: '1px solid #e2e8f0', boxSizing: 'border-box' }}>
              <section
                ref={howRevealRef}
                aria-labelledby={`${clusterId}-how-title`}
              >
                <div className="logistics-how-inner" style={{ padding: 0 }}>
                  <header className="logistics-how-header logistics-how-header-animate">
                    <p className="logistics-how-eyebrow">Process</p>
                    <h2 id={`${clusterId}-how-title`} className="logistics-how-title">
                      {cfg.howTitle}
                    </h2>
                    <p className="logistics-how-lead">{cfg.howLead}</p>
                  </header>
                  <ol className="logistics-hub-steps">
                    {cfg.steps.map((step) => (
                      <li key={step.id} className="logistics-hub-step">
                        <span className="logistics-hub-step-num" aria-hidden>
                          {step.id}
                        </span>
                        <div>
                          <h3 className="logistics-hub-step-title">{step.title}</h3>
                          <p className="logistics-hub-step-body">{step.body}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>

      {clusterId !== 'ecommerce' && (
        <RelatedLinks
          title="Related services & guides"
          serviceLinks={cfg.relatedServices}
          blogLink={cfg.relatedBlog}
        />
      )}

      <section className="logistics-closing-cta" aria-labelledby={`${clusterId}-cta-title`}>
        <div className="logistics-closing-cta-inner container">
          <h2 id={`${clusterId}-cta-title`} className="logistics-closing-cta-title">
            Ready to talk about {cfg.ariaLabel.toLowerCase()}?
          </h2>
          <p className="logistics-closing-cta-lede">
            Tell us volumes, channels and SKU profile — we will quote the right lane. Still comparing options? Browse the{' '}
            <Link to="/logistics-services">logistics services hub</Link>,{' '}
            <Link to="/shopify-fulfillment-services">Shopify fulfilment UK</Link>,{' '}
            <Link to="/amazon-fba-prep-services">Amazon FBA prep UK</Link>, or related{' '}
            <Link to="/fulfillment-blog">fulfilment blog</Link> guides.
          </p>
          <div className="logistics-closing-cta-actions">
            <div className="logistics-closing-cta-button-row">
              <Link
                to="/contact-fulfillment-services"
                className="logistics-closing-cta-btn logistics-closing-cta-btn--primary"
              >
                {cfg.ctaLabel} <span aria-hidden>→</span>
              </Link>
              <a
                href={whatsappHref}
                className="logistics-closing-cta-btn logistics-closing-cta-btn--whatsapp"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open WhatsApp about ${cfg.ariaLabel}`}
                onClick={() => trackWhatsAppClick(`logistics-cluster-${clusterId}`)}
              >
                Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      <FAQSection
        headingId={`${clusterId}-faq`}
        className="faqs-section"
        title="Frequently Asked Questions"
        faqs={cfg.faqs}
      />

      <Newsletter />
      <Footer />
    </div>
  );
};

export default LogisticsClusterPage;
